import { useEffect, useState, useCallback } from "react";
import { CloudDownload, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { prefetchOfflineData, getPrefetchMeta } from "@/lib/offlinePrefetch";
import { useOnline } from "@/lib/useOnline";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/**
 * OfflineDataCard — kompakt kort som lar brukaren:
 *  • Laste ned pantry + handleliste + båt/lokasjonar for offline-bruk
 *  • Sjå kor fersk offline-data er (sist oppdatert-tidsstempel)
 *  • Sjå status/feil frå siste prefetch
 *
 * Vises i mobilmenyen (drawer) og som ein knapp i profil/innstillingar.
 */
function formatTimestamp(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return `i dag kl. ${d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}`;
    }
    return d.toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function OfflineDataCard({ compact = false }) {
  const { online } = useOnline();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { step, total, label }

  useEffect(() => {
    let mounted = true;
    getPrefetchMeta().then((m) => { if (mounted) setMeta(m); });
    return () => { mounted = false; };
  }, []);

  const preferredShift = user?.preferred_shift || "1";

  const runPrefetch = useCallback(async () => {
    if (busy || !online) return;
    setBusy(true);
    setProgress({ step: 0, total: 8, label: "Startar…" });
    try {
      const res = await prefetchOfflineData({
        shift: preferredShift,
        onProgress: (p) => setProgress(p),
      });
      setMeta({ at: res.at, errors: res.errors });
      if (res.errors.length === 0) {
        toast.success("Offline-data er oppdatert");
      } else {
        toast.warning(`Offline-data oppdatert — ${res.errors.length} feil (sjå detaljar)`);
      }
    } catch (e) {
      toast.error("Kunne ikkje laste ned offline-data");
      console.warn("[offline prefetch] failed:", e);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }, [busy, online, preferredShift]);

  const lastAt = meta?.at ? formatTimestamp(meta.at) : null;
  const hasErrors = meta?.errors && meta.errors.length > 0;

  return (
    <div
      className={`rounded-md border ${hasErrors ? "border-amber-200 bg-amber-50" : "border-stone-200 bg-white"} px-3 py-2.5 space-y-2`}
      data-testid="offline-data-card"
    >
      <div className="flex items-center gap-2">
        <CloudDownload className="h-4 w-4 text-stone-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-stone-800">Offline-data</p>
          {lastAt ? (
            <p className="text-[11px] text-stone-500 truncate" data-testid="offline-last-updated">
              Sist oppdatert {lastAt}
            </p>
          ) : (
            <p className="text-[11px] text-stone-500">Ikkje lasta ned enno</p>
          )}
        </div>
        {hasErrors && !busy && (
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" title={`${meta.errors.length} feil`} />
        )}
        {lastAt && !hasErrors && !busy && (
          <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
        )}
      </div>

      {busy && progress && (
        <div className="text-[11px] text-stone-600 flex items-center gap-1.5" data-testid="offline-progress">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span className="truncate">{progress.step}/{progress.total} · {progress.label}</span>
        </div>
      )}

      <button
        onClick={runPrefetch}
        disabled={busy || !online}
        className={`w-full inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
          !online
            ? "bg-stone-100 text-stone-400 cursor-not-allowed"
            : busy
              ? "bg-stone-100 text-stone-500"
              : "bg-navy-800 text-white hover:bg-navy-900"
        }`}
        data-testid="offline-prefetch-btn"
        title={!online ? "Krev nettverksforbindelse" : ""}
      >
        {busy ? (
          <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Lastar ned…</>
        ) : !online ? (
          <>Offline — kan ikkje oppdatere</>
        ) : lastAt ? (
          <><CloudDownload className="h-3.5 w-3.5" /> Oppdater offline-data</>
        ) : (
          <><CloudDownload className="h-3.5 w-3.5" /> Last ned for offline</>
        )}
      </button>

      {!compact && hasErrors && (
        <details className="text-[11px] text-amber-800">
          <summary className="cursor-pointer select-none">Vis feil ({meta.errors.length})</summary>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            {meta.errors.map((e, i) => (
              <li key={i} className="truncate">{e.step}: {e.error}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
